/**
 * v-auth-img 指令
 *
 * <img> 標籤無法直接帶自訂請求頭，這裡用 axios 帶 Authorization / X-TENANT-ID
 * 取得圖片二進位後，透過 URL.createObjectURL 換掉 src。
 *
 * 特性：
 *  - 快取：同一張圖（同員工頭像出現多次）只會發一次請求。
 *  - 進行中請求去重：同一畫面同時渲染多個相同圖時共用同一個 Promise。
 *  - LRU 淘汰：超過上限才 revoke 舊 blob，避免記憶體無限增長。
 *  - 切換團隊 / 登出時請呼叫 clearAuthImgCache() 清空快取。
 *
 * 用法：
 *   1) 一般（用當前 localStorage 的 teamId 當 X-TENANT-ID）：
 *      <img v-auth-img="item.avatarUrl" data-fallback="/default-avatar.png" />
 *
 *   2) 指定 tenantId（如團隊列表，每列是不同團隊，需各自的 X-TENANT-ID）：
 *      <img v-auth-img="{ url: row.logo, tenantId: row.id }" />
 */
import axios from 'axios';
import { AuthManage } from '@/plugins/auth';

// cacheKey -> objectURL（已完成的結果）
const urlCache = new Map();
// cacheKey -> Promise<objectURL>（進行中的請求，用來去重）
const pendingCache = new Map();

const MAX_CACHE = 200;

// 把指令的值正規化成 { url, tenantId }
function normalize(value) {
  if (!value) return { url: '', tenantId: null };
  if (typeof value === 'string') {
    return { url: value, tenantId: null };
  }
  return { url: value.url || '', tenantId: value.tenantId || null };
}

// 快取 key 需含 tenantId：同一 url 在不同租戶下屬不同資源
function cacheKey(url, tenantId) {
  return `${tenantId || ''}::${url}`;
}

function setCache(key, objectUrl) {
  // 超過上限時淘汰最舊一筆並釋放其 blob
  if (urlCache.size >= MAX_CACHE) {
    const oldestKey = urlCache.keys().next().value;
    const oldestUrl = urlCache.get(oldestKey);
    if (oldestUrl) URL.revokeObjectURL(oldestUrl);
    urlCache.delete(oldestKey);
  }
  urlCache.set(key, objectUrl);
}

function fetchAsObjectUrl(url, tenantId) {
  // tenantId 未指定時退回當前 localStorage 的 teamId
  const tid = tenantId || localStorage.getItem('teamId') || '';
  const key = cacheKey(url, tid);

  // 已有結果，直接回傳
  if (urlCache.has(key)) {
    return Promise.resolve(urlCache.get(key));
  }
  // 已有相同請求在飛，共用同一個 Promise
  if (pendingCache.has(key)) {
    return pendingCache.get(key);
  }

  const promise = axios
    .get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${AuthManage.token}`,
        'X-TENANT-ID': tid
      }
    })
    .then((res) => {
      const objectUrl = URL.createObjectURL(res.data);
      setCache(key, objectUrl);
      pendingCache.delete(key);
      return objectUrl;
    })
    .catch((err) => {
      pendingCache.delete(key);
      throw err;
    });

  pendingCache.set(key, promise);
  return promise;
}

// 1x1 透明佔位圖：載入 / 失敗時填入，避免瀏覽器渲染「裂圖」佔位符
const TRANSPARENT_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

async function loadImage(el, value, fallback) {
  const { url, tenantId } = normalize(value);
  if (!url) {
    // 沒有 url 時也給透明佔位，避免空 src 裂圖
    el.src = fallback || TRANSPARENT_PLACEHOLDER;
    return;
  }
  // 記住這次要載入的 key，避免元素被複用（快速切換）時把舊結果蓋上去
  const tid = tenantId || localStorage.getItem('teamId') || '';
  const key = cacheKey(url, tid);
  el._authImgKey = key;
  // 請求進行中先填透明佔位（若尚未有內容），避免裂圖
  if (!el.src || el.src === 'about:blank') {
    el.src = TRANSPARENT_PLACEHOLDER;
  }
  try {
    const objectUrl = await fetchAsObjectUrl(url, tenantId);
    // 以 _authImgKey 守衛競態，非本次載入就不覆蓋
    // eslint-disable-next-line require-atomic-updates
    if (el._authImgKey === key) el.src = objectUrl;
  } catch (e) {
    // eslint-disable-next-line require-atomic-updates
    if (el._authImgKey === key) el.src = fallback || TRANSPARENT_PLACEHOLDER;
  }
}

/**
 * 供元件（如 view-design 的 <Avatar>）取用：帶 header 取得圖片並回傳 blob URL。
 * 與指令共用同一份快取 / 去重 / LRU 邏輯。
 * @param {string} url 圖片位址
 * @param {string} [tenantId] 指定 X-TENANT-ID，省略時用當前 teamId
 * @returns {Promise<string>} 可直接綁到 :src 的 objectURL
 */
export function fetchAuthImg(url, tenantId) {
  if (!url) return Promise.resolve('');
  return fetchAsObjectUrl(url, tenantId);
}

/**
 * 清空並釋放所有快取的 blob URL。
 * 切換團隊（X-TENANT-ID 改變）或登出時呼叫。
 */
export function clearAuthImgCache() {
  urlCache.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  urlCache.clear();
  pendingCache.clear();
}

// 判斷指令值是否有變（支援字串或 { url, tenantId }）
function isSameValue(a, b) {
  const na = normalize(a);
  const nb = normalize(b);
  return na.url === nb.url && na.tenantId === nb.tenantId;
}

export default {
  bind(el, binding) {
    loadImage(el, binding.value, el.dataset.fallback);
  },
  update(el, binding) {
    if (!isSameValue(binding.value, binding.oldValue)) {
      loadImage(el, binding.value, el.dataset.fallback);
    }
  }
  // 注意：objectURL 由快取共用，不能在 unbind 時 revoke，
  // 否則同員工其他仍在顯示的頭像會變破圖。生命週期交由 LRU / clearAuthImgCache 管理。
};
